import React, {useCallback, useRef, useEffect, useState} from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import type Player from 'video.js/dist/types/player';
import {ISubtitleTrack} from "../models/interfaces/subtitleTrack.interface.ts";
import {
  Box, Flex, IconButton, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
  Menu, MenuButton, MenuList, MenuItem, Text
} from "@chakra-ui/react";
import {FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaExpand, FaCompress} from "react-icons/fa";
import {TbRewindBackward15, TbRewindForward15} from "react-icons/tb";
import {MdOutlineReplay5, MdOutlineForward5} from "react-icons/md";
import {BiCaptions} from "react-icons/bi";

const formatTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
};

const shouldPreserveNativeSpace = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const interactiveElement = target.closest<HTMLElement>('a[href], button, input, select, textarea, [role="button"], [role="checkbox"], [role="menuitem"], [role="option"], [role="slider"], [role="switch"], [role="tab"]');
  if (interactiveElement === null) return false;
  const isMediaPlayerSlider = interactiveElement.matches('[role="slider"]') && interactiveElement.closest(".glix-media-player") !== null;
  return !isMediaPlayerSlider && interactiveElement.closest(".media-player-transport") === null;
};

const MEDIA_PLAYER_STYLES = `
.glix-media-player {
  container-type: inline-size;
  container-name: glix-player;
}
.glix-media-player .vjs-text-track-display {
  inset-inline: 0 !important;
  inset-block: 0 max(12px, 3%) !important;
}
.glix-media-player .vjs-text-track-cue {
  line-height: 0 !important;
  background: transparent !important;
}
.glix-media-player .vjs-text-track-cue > div {
  display: inline-block !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  color: white !important;
  font-size: clamp(15px, calc(10px + 1.15cqw), 30px) !important;
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif !important;
  font-weight: 500 !important;
  line-height: 1.32 !important;
  padding: 0.16em 0.5em !important;
  border-radius: 0.22em !important;
  max-width: 88% !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.55);
}
.glix-media-player video::cue {
  background-color: rgba(0, 0, 0, 0.5);
  line-height: 1.32;
  font-weight: 500;
}
.media-player-controls {
  --glix-bar-height: 56px;
  --glix-control-size: 40px;
  --glix-icon-size: 18px;
  --glix-play-size: 44px;
  --glix-control-gap: 8px;
  --glix-bar-padding: 12px;
  --glix-time-size: 13px;
  --glix-volume-width: 72px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  grid-template-areas: "time transport utilities";
  align-items: center;
  height: var(--glix-bar-height);
  padding-inline: var(--glix-bar-padding);
  column-gap: var(--glix-bar-padding);
}
.media-player-time {
  grid-area: time;
  justify-self: start;
  min-width: 0;
}
.media-player-transport {
  grid-area: transport;
  justify-self: center;
}
.media-player-utilities {
  grid-area: utilities;
  justify-self: end;
  min-width: 0;
}
.glix-media-player .media-player-control-button {
  width: var(--glix-control-size) !important;
  min-width: var(--glix-control-size) !important;
  height: var(--glix-control-size) !important;
  padding: 0 !important;
  border-radius: 8px !important;
  background: transparent !important;
  color: rgba(255, 255, 255, 0.82) !important;
  font-size: var(--glix-icon-size) !important;
  transition-property: background-color, color, transform;
  transition-duration: 160ms;
}
.glix-media-player .media-player-control-button:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  color: white !important;
}
.glix-media-player .media-player-control-button:active {
  background: rgba(255, 255, 255, 0.18) !important;
  transform: scale(0.96);
}
.glix-media-player .media-player-play-button {
  width: var(--glix-play-size) !important;
  min-width: var(--glix-play-size) !important;
  height: var(--glix-play-size) !important;
  border-radius: 9999px !important;
  background: var(--chakra-colors-brandPurple-500) !important;
  color: white !important;
  box-shadow: 0 2px 10px rgba(135, 50, 244, 0.35);
}
.glix-media-player .media-player-play-button:hover {
  background: var(--chakra-colors-brandPurple-400) !important;
}
.glix-media-player .media-player-seek-thumb {
  transition-property: transform, box-shadow;
  transition-duration: 160ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
}
.glix-media-player .media-player-seek-thumb:hover {
  transform: scale(1.15);
  box-shadow: 0 0 0 4px rgba(135, 50, 244, 0.22), 0 1px 3px rgba(0, 0, 0, 0.5);
}
.glix-media-player .media-player-seek-thumb:focus-visible {
  box-shadow: 0 0 0 4px rgba(135, 50, 244, 0.32), 0 1px 3px rgba(0, 0, 0, 0.5);
}
.glix-media-player .media-player-rate-control {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--glix-control-size);
  min-width: calc(var(--glix-control-size) * 1.1);
  padding-inline: 8px;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.82);
  font-size: var(--glix-time-size);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}
.glix-media-player .media-player-rate-control:hover {
  background: rgba(255, 255, 255, 0.12);
  color: white;
}
@container glix-player (max-width: 819px) {
  .media-player-controls {
    --glix-bar-height: 80px;
    --glix-control-size: 40px;
    --glix-icon-size: 16px;
    --glix-play-size: 40px;
    --glix-control-gap: 4px;
    --glix-bar-padding: 6px;
    --glix-time-size: 11px;
    --glix-volume-width: 44px;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: 40px 40px;
    grid-template-areas: "time utilities" "transport transport";
  }
}
@container glix-player (max-width: 479px) {
  .media-player-controls {
    --glix-bar-height: 112px;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 32px 40px 40px;
    grid-template-areas: "time" "utilities" "transport";
  }
  .media-player-time,
  .media-player-transport,
  .media-player-utilities {
    justify-self: center;
  }
}
@container glix-player (min-width: 1200px) {
  .media-player-controls {
    --glix-bar-height: 64px;
    --glix-icon-size: 20px;
    --glix-play-size: 48px;
    --glix-control-gap: 10px;
    --glix-bar-padding: 16px;
    --glix-time-size: 14px;
    --glix-volume-width: 88px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .glix-media-player .media-player-seek-thumb {
    transition-duration: 0ms;
  }
}
`;

interface VideoJSProps {
  options: Parameters<typeof videojs>[1];
  onReady?: (player: Player) => void;
  onTimeUpdate?: (time: number) => void;
  subtitles?: ISubtitleTrack[];
  subtitleLanguageCode?: string;
  fitMode?: 'fill' | 'intrinsic';
  getVideoDimensions?: () => {width: number; height: number} | null;
}

const VideoJS: React.FC<VideoJSProps> = ({options, onReady, onTimeUpdate, subtitles, subtitleLanguageCode, fitMode = 'fill', getVideoDimensions}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const regionRef = useRef<HTMLDivElement | null>(null);
  const intrinsicMetadataRef = useRef<(() => void) | null>(null);
  const [intrinsicAspect, setIntrinsicAspect] = useState(16 / 9);
  const [regionSize, setRegionSize] = useState({width: 0, height: 0});

  useEffect(() => {
    if (fitMode !== 'intrinsic') return;
    const video = videoRef.current;
    const updateAspect = () => {
      const dimensions = getVideoDimensions ? getVideoDimensions() : video && {width: video.videoWidth, height: video.videoHeight};
      const aspect = dimensions ? dimensions.width / dimensions.height : 0;
      setIntrinsicAspect(dimensions && dimensions.width > 0 && dimensions.height > 0 && Number.isFinite(aspect) && aspect > 0 ? aspect : 16 / 9);
    };
    intrinsicMetadataRef.current = updateAspect;
    video?.addEventListener('loadedmetadata', updateAspect);
    updateAspect();
    return () => {
      intrinsicMetadataRef.current = null;
      video?.removeEventListener('loadedmetadata', updateAspect);
    };
  }, [fitMode, getVideoDimensions]);

  useEffect(() => {
    if (fitMode !== 'intrinsic') return;
    const region = regionRef.current!;
    let active = true;
    const updateSize = ({width, height}: {width: number; height: number}) => {
      if (active) setRegionSize({width, height});
    };
    const measure = () => updateSize(region.getBoundingClientRect());
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => {
        active = false;
        window.removeEventListener('resize', measure);
      };
    }
    const observer = new ResizeObserver(([entry]) => updateSize(entry.contentRect));
    observer.observe(region);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [fitMode]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [subtitleTracks, setSubtitleTracks] = useState<TextTrack[]>([]);
  const [activeSubtitle, setActiveSubtitle] = useState<string | null>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isPlaybackBlocked, setIsPlaybackBlocked] = useState(false);
  const remoteTrackElementsRef = useRef<ReturnType<Player['addRemoteTextTrack']>[]>([]);

  const requestPlayback = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const playback = player.play();
    if (!playback) {
      setIsPlaybackBlocked(false);
      return;
    }
    playback.then(() => setIsPlaybackBlocked(false)).catch(() => setIsPlaybackBlocked(true));
  }, []);

  const togglePlayback = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (player.paused()) {
      requestPlayback();
    } else {
      player.pause();
    }
  }, [requestPlayback]);

  useEffect(() => {
    const interval = setInterval(() => {
      const videoElement = videoRef.current;

      if (videoElement && videoElement.offsetParent !== null && !playerRef.current) {
        clearInterval(interval);
        playerRef.current = videojs(videoElement, options, () => {
          if (onReady && playerRef.current) onReady(playerRef.current);
          setIsPlayerReady(true);
          intrinsicMetadataRef.current?.();
          if (options?.autoplay) requestPlayback();
        });

        playerRef.current.on("play", () => {
          setIsPlaying(true);
          setIsPlaybackBlocked(false);
        });
        playerRef.current.on("pause", () => setIsPlaying(false));
        playerRef.current.on("timeupdate", () => {
          const time = playerRef.current?.currentTime() ?? 0;
          setCurrentTime(time);
          onTimeUpdate?.(time);
        });
        playerRef.current.on("loadedmetadata", () => {
          setDuration(playerRef.current?.duration() ?? 1);
          intrinsicMetadataRef.current?.();
        });
        playerRef.current.on("volumechange", () => {
          setVolume(playerRef.current?.volume() ?? 1);
          setMuted(playerRef.current?.muted() ?? false);
        });
        playerRef.current.on("ratechange", () => {
          setPlaybackRate(playerRef.current?.playbackRate() ?? 1);
        });
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [options, onReady, onTimeUpdate, requestPlayback]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isSpace = event.key === " " || event.code === "Space";
      if (!isSpace || event.defaultPrevented || event.repeat || shouldPreserveNativeSpace(event.target)) return;
      event.preventDefault();
      togglePlayback();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [togglePlayback]);

  useEffect(() => {
    if (!playerRef.current || !isPlayerReady) return;
    const player = playerRef.current;

    remoteTrackElementsRef.current.forEach(trackEl => {
      player.removeRemoteTextTrack(trackEl);
    });
    remoteTrackElementsRef.current = [];

    const loadedTracks: TextTrack[] = [];
    subtitles?.forEach(({src, label, srclang, default: isDefault}) => {
      const trackElement = player.addRemoteTextTrack(
        {kind: 'subtitles', src, srclang, label, default: isDefault},
        false
      );
      remoteTrackElementsRef.current.push(trackElement);
      const nativeTrack = (trackElement as unknown as { track: TextTrack }).track;
      if (nativeTrack) loadedTracks.push(nativeTrack);
    });
    setSubtitleTracks(loadedTracks);
  }, [subtitles, isPlayerReady]);

  useEffect(() => {
    if (!playerRef.current || !isPlayerReady || subtitleTracks.length === 0) return;

    const active = subtitleTracks.find(t => t.mode === 'showing');
    const selectedTrack = subtitleTracks.find(track => track.language === subtitleLanguageCode);
    if (selectedTrack) {
      subtitleTracks.forEach(track => track.mode = 'disabled');
      selectedTrack.mode = 'showing';
    }
    const defaultTrack = subtitles?.find(s => s.default)?.label;
    setActiveSubtitle(selectedTrack?.label || active?.label || defaultTrack || 'Off');
  }, [subtitleLanguageCode, subtitleTracks, isPlayerReady, subtitles]);

  const handlePlaybackChange = (rate: number) => {
    if (!playerRef.current) return;
    playerRef.current.playbackRate(rate);
    setPlaybackRate(rate);
  };

  const stageWidth = Math.min(regionSize.width, regionSize.height * intrinsicAspect);
  const videoElement = (
    <video
      data-testid="media-player-video"
      ref={videoRef}
      className="video-js vjs-fill vjs-default-skin"
      playsInline
      preload="metadata"
      style={fitMode === 'intrinsic'
        ? {width: '100%', height: '100%', objectFit: 'contain', background: 'transparent'}
        : {width: '100%', height: '100%', objectFit: 'cover'}}
    />
  );

  return (
    <Flex data-testid="media-player-shell" className="glix-media-player" style={{width: '100%', height: '100%', minHeight: 0}} direction="column">
      <style data-testid="media-player-styles">{MEDIA_PLAYER_STYLES}</style>
      <Flex data-testid={fitMode === 'fill' ? 'media-player-stage' : undefined} style={{width: '100%', height: '100%', minHeight: 0}} position="relative" direction="column">
        {/* Video */}
        <Box
          data-testid="media-player-video-region"
          ref={regionRef}
          data-vjs-player={fitMode === 'fill' ? true : undefined}
          position="relative"
          style={fitMode === 'intrinsic'
            ? {flex: '1 1 0%', minHeight: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}
            : {flex: '1 1 0%', minHeight: 0, overflow: 'hidden'}}
          onClick={() => {
            if (!document.fullscreenElement) togglePlayback();
          }}
          onDoubleClick={() => {
            if (isFullscreen) {
              document.exitFullscreen();
            } else {
              videoRef.current?.requestFullscreen();
            }
            setIsFullscreen(!isFullscreen);
          }}
        >
          {fitMode === 'intrinsic' ? (
            <Box data-testid="media-player-stage" position="relative" style={{width: stageWidth, height: stageWidth / intrinsicAspect, flexShrink: 0}}>
              {/* Video.js ingests and restyles its host; keep the measured stage outside it. */}
              <Box data-vjs-player style={{width: '100%', height: '100%'}}>{videoElement}</Box>
            </Box>
          ) : videoElement}
          {isPlaybackBlocked && (
            <Flex
              data-testid="media-player-blocked-overlay"
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              direction="column"
              gap={3}
              bg="blackAlpha.600"
              onClick={(event) => event.stopPropagation()}
            >
              <IconButton
                className="media-player-control-button media-player-play-button"
                aria-label="Start playback"
                icon={<FaPlay/>}
                onClick={requestPlayback}
                isRound
                size="lg"
              />
              <Text color="white" fontSize="sm" textAlign="center" px={4}>
                Your browser blocked automatic playback. Press play to start.
              </Text>
            </Flex>
          )}
        </Box>

        {/* Slider */}
        <Box
          data-testid="media-player-seek-row"
          w="full"
          bg="gray.800"
          style={{height: "16px", flexShrink: 0}}
        >
          <Slider
            data-testid="media-player-seek"
            min={0}
            max={duration}
            step={1}
            value={currentTime}
            onChange={(val) => playerRef.current?.currentTime(Number(val))}
            aria-label="Seek"
            w="full"
            h="full"
          >
            <SliderTrack bg="whiteAlpha.300" h="4px" borderRadius="full">
              <SliderFilledTrack bg="brandPurple.400"/>
            </SliderTrack>
            <SliderThumb className="media-player-seek-thumb" boxSize="12px" bg="white" border="none" boxShadow="0 1px 3px rgba(0, 0, 0, 0.5)"/>
          </Slider>
        </Box>

        {/* Controls */}
        <Box
          data-testid="media-player-controls"
          className="media-player-controls"
          w="full"
          flexShrink={0}
          bg="gray.800"
        >
          <Flex data-testid="media-player-time" className="media-player-time" align="center">
            <Text color="white" fontSize="var(--glix-time-size)" fontWeight="500" whiteSpace="nowrap" style={{fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em'}}>
              {formatTime(currentTime)}
              <Box as="span" color="whiteAlpha.600" px="0.35em">/</Box>
              <Box as="span" color="whiteAlpha.600">{formatTime(duration)}</Box>
            </Text>
          </Flex>

          <Flex data-testid="media-player-transport" className="media-player-transport" align="center" gap="var(--glix-control-gap)">
            <IconButton
              className="media-player-control-button"
              aria-label="Rewind 15s"
              icon={<TbRewindBackward15/>}
              onClick={() => playerRef.current?.currentTime(currentTime - 15)}
            />
            <IconButton
              className="media-player-control-button"
              aria-label="Rewind 5s"
              icon={<MdOutlineReplay5/>}
              onClick={() => playerRef.current?.currentTime(currentTime - 5)}
            />
            <IconButton
              className="media-player-control-button media-player-play-button"
              aria-label="Play/Pause"
              aria-keyshortcuts="Space"
              icon={isPlaying ? <FaPause/> : <FaPlay/>}
              onClick={togglePlayback}
            />
            <IconButton
              className="media-player-control-button"
              aria-label="Forward 5s"
              icon={<MdOutlineForward5/>}
              onClick={() => playerRef.current?.currentTime(currentTime + 5)}
            />
            <IconButton
              className="media-player-control-button"
              aria-label="Forward 15s"
              icon={<TbRewindForward15/>}
              onClick={() => playerRef.current?.currentTime(currentTime + 15)}
            />
          </Flex>

          <Flex data-testid="media-player-utilities" className="media-player-utilities" align="center" gap="var(--glix-control-gap)">
              <Menu>
                <MenuButton
                  as={Box}
                  className="media-player-rate-control"
                  aria-label="Playback speed"
                >
                  <Text>{`x${playbackRate}`}</Text>
                </MenuButton>
                <MenuList
                  bg="gray.800"
                  border="1px solid"
                  borderColor="gray.700"
                  shadow="lg"
                  rounded="md"
                  minW="150px"
                  p={2}
                >
                  {[0.25, 0.5, 1, 1.25, 1.5, 2].map(rate => (
                    <MenuItem
                      key={rate}
                      bg={playbackRate == rate ? "brandPurple.500" : ""}
                      color="white"
                      onClick={() => handlePlaybackChange(rate)}
                      _hover={{bg: "brandPurple.500", opacity: 0.8}}
                    >
                      x{rate}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Menu>
                <MenuButton
                  as={IconButton}
                  className="media-player-control-button"
                  aria-label="Subtitles"
                  icon={<BiCaptions color="white"/>}
                />
                <MenuList
                  bg="gray.800"
                  border="1px solid"
                  borderColor="gray.700"
                  shadow="lg"
                  rounded="md"
                  minW="150px"
                  p={2}
                >
                  <MenuItem
                    onClick={() => {
                      subtitleTracks.forEach(t => t.mode = 'disabled');
                      setActiveSubtitle('Off');
                    }}
                    color="white"
                    bg={activeSubtitle === 'Off' ? 'brandPurple.500' : ""}
                    _hover={{bg: "brandPurple.500", opacity:0.8}}
                  >
                    Off
                  </MenuItem>
                  {subtitleTracks.map((track, idx) => (
                    <MenuItem
                      key={idx}
                      onClick={() => {
                        subtitleTracks.forEach(t => t.mode = 'disabled');
                        track.mode = 'showing';
                        setActiveSubtitle(track.label);
                      }}
                      color="white"
                      bg={activeSubtitle === track.label ? 'brandPurple.500' : ""}
                      _hover={{bg: "brandPurple.500", opacity:0.8}}
                    >
                      {track.label || `Track ${idx + 1}`}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Flex align="center" gap="var(--glix-control-gap)">
                <IconButton
                  className="media-player-control-button"
                  aria-label="Mute"
                  icon={muted ? <FaVolumeMute/> : <FaVolumeUp/>}
                  onClick={() => {
                    const newMuted = !muted;
                    playerRef.current?.muted(newMuted);
                    setMuted(newMuted);
                  }}
                />
                <Slider
                  aria-label="Volume"
                  w="var(--glix-volume-width)"
                  min={0}
                  max={1}
                  step={0.05}
                  value={volume}
                  onChange={(val) => {
                    setVolume(val);
                    playerRef.current?.volume(val);
                  }}
                >
                  <SliderTrack bg="whiteAlpha.300" h="3px" borderRadius="full"><SliderFilledTrack bg="brandPurple.400"/></SliderTrack>
                  <SliderThumb boxSize="12px" bg="white" border="none" boxShadow="0 1px 3px rgba(0, 0, 0, 0.45)"/>
                </Slider>
              </Flex>
              <IconButton
                className="media-player-control-button"
                aria-label="Fullscreen"
                icon={isFullscreen ? <FaCompress/> : <FaExpand/>}
                onClick={() => {
                  const fullscreen = !!document.fullscreenElement;
                  if (fullscreen) {
                    document.exitFullscreen().catch((err) => console.error(err));
                  } else {
                    videoRef.current?.requestFullscreen();
                  }
                  setIsFullscreen(!fullscreen);
                }}
              />
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

export default VideoJS;
